import { useContext, useState } from 'react'

import { Heading } from '@hhgtech/hhg-components'
import { Text } from '@hhgtech/hhg-components/mantine'
import { ArrowLeft, ArrowRight } from '@hhgtech/icons/other'
import { Carousel, Embla } from '@mantine/carousel'
import { Box, BoxProps, Flex, MantineTheme, Stack, rem } from '@mantine/core'
import { useIntl } from 'react-intl'

import { useCurrentUrl } from 'hooks/useCurrentUrl'
import { useIsMobile } from 'hooks/useIsMobile'
import { useLeavePageConfirm } from 'hooks/useLeavePageConfirm'
import { MedicalFact, SpotlightMedicalFactItem } from 'interfaces/types'

import useStyles from './index.styles'
import { HeroBannerContext } from '../context'

const Item = ({ content, link }: SpotlightMedicalFactItem) => {
  const { formatMessage: f } = useIntl()
  const { hasLeavePageConfirm } = useContext(HeroBannerContext)
  const { handleClick } = useLeavePageConfirm(link.url)
  return (
    <Stack spacing={rem(16)} justify="space-between">
      <Text
        size="s3"
        weight="semiBold"
        as="div"
        dangerouslySetInnerHTML={{
          __html: content,
        }}
      />
      {link.url && (
        <Box>
          <a
            className="pointer-event-child-none"
            href={link.url}
            target={'_blank'}
            style={{ display: 'inline-block' }}
            data-event-category="Service Connection"
            data-event-action="Medical Fact Read More"
            data-event-label={link.url}
            {...(hasLeavePageConfirm && {
              onClick: handleClick,
            })}
          >
            <Box
              sx={(theme) => ({
                display: 'flex',
                padding: `${rem(4)} 0`,
                [theme.fn.smallerThan('sm')]: {
                  flexDirection: 'column',
                  gap: rem(2),
                },
                [theme.fn.largerThan('sm')]: {
                  gap: rem(8),
                  alignItems: 'center',
                  textDecoration: 'underline',
                  textUnderlineOffset: rem(8),
                },
              })}
            >
              <Text
                size="p3"
                as="span"
                sx={(theme) => ({
                  [theme.fn.largerThan('sm')]: { lineHeight: rem(36) },
                })}
              >
                <Text
                  size="p3"
                  as="span"
                  color="gray.6"
                  sx={(theme) => ({
                    [theme.fn.smallerThan('sm')]: { display: 'block' },
                  })}
                >
                  {f({ id: 'articlePage.readMore' })}:&nbsp;&nbsp;
                </Text>
                <Text
                  size="p2"
                  as="span"
                  sx={(theme) => ({
                    [theme.fn.smallerThan('sm')]: {
                      textDecoration: 'underline',
                      textUnderlineOffset: rem(8),
                      lineHeight: rem(30),
                    },
                  })}
                >
                  <span>{link.title}</span>
                  <span style={{ position: 'relative' }}>
                    &nbsp;&nbsp;&nbsp;&nbsp;
                    <ArrowRight
                      size={16}
                      style={{
                        position: 'absolute',
                        bottom: rem(1),
                        right: '-3px',
                        verticalAlign: 'middle',
                        display: 'inline-block',
                      }}
                    />
                  </span>
                </Text>
              </Text>
            </Box>
          </a>
        </Box>
      )}
    </Stack>
  )
}

const MedicalFacts = ({
  medicalFacts,
  className,
  classNames,
  styles,
  ...boxProps
}: { medicalFacts: MedicalFact } & BoxProps) => {
  const { classes, cx } = useStyles(undefined, {
    name: 'MedicalFacts',
    classNames,
    styles,
  })
  const [embla, setEmbla] = useState<Embla | null>(null)
  const { title, listContent = [] } = medicalFacts
  const isMobile = useIsMobile()
  const currentUrl = useCurrentUrl()
  const btnProps = {
    sx: (theme: MantineTheme) => ({
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      borderRadius: '50%',
      width: rem(40),
      height: rem(40),
      border: `1px solid ${theme.colors.neutral[1]}`,
      background: 'white',
      cursor: 'pointer',
      transition: 'all .2s ease',
      '&:hover': {
        background: theme.colors.neutral[1],
      },
    }),
  }

  const controls =
    listContent.length > 1 ? (
      <Flex gap={rem(16)}>
        <Box
          className="pointer-event-child-none"
          {...btnProps}
          data-event-category="Service Connection"
          data-event-action="Medical Fact Carousel Click"
          data-event-label={currentUrl}
          onClick={() => embla?.scrollPrev()}
        >
          <ArrowLeft size={20} />
        </Box>
        <Box
          className="pointer-event-child-none"
          {...btnProps}
          data-event-category="Service Connection"
          data-event-action="Medical Fact Carousel Click"
          data-event-label={currentUrl}
          onClick={() => embla?.scrollNext()}
        >
          <ArrowRight size={20} />
        </Box>
      </Flex>
    ) : null

  return listContent.length > 0 ? (
    <Box className={cx(classes.root, className)} {...boxProps}>
      {title && (
        <Stack
          justify="space-between"
          sx={(theme) => ({
            [theme.fn.largerThan('sm')]: {
              minWidth: rem(320),
            },
          })}
        >
          {title && (
            <Heading tag="h3" className={classes.heading}>
              {title}
            </Heading>
          )}
          {!isMobile && controls}
        </Stack>
      )}
      <Box>
        {listContent.length > 1 ? (
          <Carousel withControls={false} getEmblaApi={setEmbla} loop>
            {listContent.map((i, idx) => (
              <Carousel.Slide key={String(idx)}>
                <Item {...i} />
              </Carousel.Slide>
            ))}
          </Carousel>
        ) : (
          <Item {...listContent?.[0]} />
        )}
      </Box>
      {isMobile && controls}
    </Box>
  ) : null
}

export { MedicalFacts }
